import { useStore } from '@/plugins/vuex';
import StemdoerService from '@/sections/admin/entities/stemdoer/stemdoer.service';
import { Stemdoer, Stemdoers } from '@/shared/model/stemdoer.model';
import { computed, defineComponent, inject, onMounted, ref, watch } from 'vue';
import StemdoersList from './stemdoers-list/StemdoersList.vue';
import StemdoersFilter from './stemdoers-filter/StemdoersFilter.vue';
import SelectionMultipleFilter from '@/shared/model/filter/single/selection-multiple-filter.model';
import Filters from '@/shared/model/filter/filters.model';
import TextFilter from '@/shared/model/filter/single/text-filter.model';
import FilterGroupOr from '@/shared/model/filter/group/filter-group-or.model';
import CompanyService from '@/sections/admin/entities/company/company.service';
import { Company } from '@/shared/model/company.model';
import ProjectService from '@/sections/admin/entities/project/project.service';
import { Projects } from '@/shared/model/project.model';

export default defineComponent({
  components: {
    StemdoersList,
    StemdoersFilter
  },
  setup() {
    const store = useStore();
    const stemdoerService: StemdoerService = inject('stemdoerService');
    const stemdoers = ref<Stemdoers>(Stemdoers.empty());
    const companyService = inject('companyService') as CompanyService;
    const projectService = inject('projectService') as ProjectService;

    const filteredStemdoers = ref<Stemdoers>(Stemdoers.empty());
    const isLoading = ref(true);
    const displaySkeleton = computed(() => {
      return isLoading.value || (stemdoers.value.length === 0 && isLoading.value);
    });
    const filters = ref<Filters>(new Filters('AND'));
    const companyData = ref<Company[]>([]);
    const companyItems = computed(() => {
      return companyData.value && companyData.value.length > 0 ? companyData.value : [];
    });
    const projectData = ref<any[]>([]);
    const projects = ref<Projects>(Projects.empty());
    const projectItems = computed(() => {
      return projectData.value && projectData.value.length > 0 ? projectData.value : [];
    });

    const fetchCompanies = async () => {
      try {
        const companies = await companyService.retrieve();
        companyData.value = companies
          .map(company => ({
            id: company.id,
            desc: company.name,
            cif: company.cif
          }))
          .sort((a, b) => a.desc.localeCompare(b.desc));
      } catch (error) {
        console.error('Error fetching companies:', error);
      }
    };

    const fetchProjects = async () => {
      try {
        const res = await projectService.retrieveAll();
        projects.value = Projects.from(res.data);
        projectData.value = res.data
          .map(project => ({
            id: project.id,
            desc: project.name,
            cif: project.companyCif
          }))
          .sort((a, b) => a.desc.localeCompare(b.desc));
      } catch (error) {
        console.error('Error fetching projects:', error);
      }
    };

    onMounted(() => {
      fetchCompanies();
      fetchProjects();
      retrieveAllStemdoersByManager().then(() => {
        filteredStemdoers.value = filters.value.filter(stemdoers.value) as Stemdoers;
      });
    });

    watch(
      () => filters.value,
      f => {
        filteredStemdoers.value = f.filter(stemdoers.value) as Stemdoers;
      },
      { deep: true }
    );

    const retrieveAllStemdoersByManager = () => {
      isLoading.value = true;
      return stemdoerService
        .retrieveStemdoersByDate(new Date())
        .then(res => {
          const stemdoersWithCurrentProject = res.data.map(sd => {
            const stemdoerInstance = new Stemdoer(sd);
            const currentProject =
              typeof stemdoerInstance._currentProject === 'function' ? stemdoerInstance._currentProject() : sd.currentProject ?? null;
            return {
              ...sd,
              currentProject
            };
          });
          stemdoers.value = Stemdoers.from(stemdoersWithCurrentProject);
          filteredStemdoers.value = Stemdoers.from(stemdoersWithCurrentProject);
          isLoading.value = false;
        })
        .catch(err => {
          console.error(err);
        });
    };

    const handleVerticalChange = verticalIds => {
      filters.value.add(new SelectionMultipleFilter('vertical.id', verticalIds));
      store.commit('pagesStore/manager/stemdoers/setVerticalFilter', verticalIds);
    };

    function buildStatusFilters(statusIds) {
      const filtersToAdd = [];
      if (statusIds.length > 1) {
        filtersToAdd.push({
          id: 'customStatusOr',
          filter: entity => {
            if (statusIds.includes('preHirable') && entity.availability?.preHirable === true) return true;
            if (statusIds.includes('inactive') && entity.hide === true) return true;
            if (statusIds.includes('active') && entity.hide === false) return true;
            return false;
          },
          toString: () => 'customStatusOr'
        });
      } else {
        if (statusIds.includes('preHirable')) {
          filtersToAdd.push(new SelectionMultipleFilter('availability.preHirable', [true]));
        }
        const otherStatuses = statusIds.filter(id => id !== 'preHirable');
        if (otherStatuses.length > 0) {
          const hideValues = otherStatuses.map(s => s === 'inactive');
          filtersToAdd.push(new SelectionMultipleFilter('hide', hideValues));
        }
      }
      return filtersToAdd;
    }

    const handleStatusChange = statusIds => {
      filters.value.rootGroup.filters = filters.value.rootGroup.filters.filter(f => {
        return !(f.id === 'status' || f.id === 'hide' || f.id === 'availability.preHirable');
      });

      if (statusIds.includes('VIEW_ALL')) {
        store.commit('pagesStore/manager/stemdoers/setStatusFilter', statusIds);
        return;
      }

      const filtersToAdd = buildStatusFilters(statusIds);
      filtersToAdd.forEach(f => filters.value.add(f));
      store.commit('pagesStore/manager/stemdoers/setStatusFilter', statusIds);
    };

    const handleSearchChange = searchTerm => {
      searchTerm = searchTerm.toLowerCase();
      const groupOr = new FilterGroupOr('searchText');
      groupOr.add(new TextFilter('vertical.name', searchTerm));
      groupOr.add(new TextFilter('name', searchTerm));
      groupOr.add(new TextFilter('surname', searchTerm));
      groupOr.add(new TextFilter('email', searchTerm));
      groupOr.add(new TextFilter('position', searchTerm));
      groupOr.add({
        id: 'companyName',
        filter: entity => {
          const project = entity.currentProject;
          if (!project) return false;
          return typeof project.companyName === 'string' && project.companyName.toLowerCase().includes(searchTerm);
        },
        toString: () => 'companyName'
      });
      groupOr.add({
        id: 'projectName',
        filter: entity => {
          const project = entity.currentProject;
          if (!project) return false;
          return typeof project.name === 'string' && project.name.toLowerCase().includes(searchTerm);
        },
        toString: () => 'projectName'
      });
      filters.value.add(groupOr);
      store.commit('pagesStore/manager/stemdoers/setSearchFilter', searchTerm);
    };

    const getCompanyIdByProject = (projectId: string): string | undefined => {
      const project = projectItems.value.find((p: any) => p.id === projectId);
      if (!project || !project.cif) return undefined;
      const company = companyItems.value.find((c: any) => c.cif === project.cif || c.id === project.cif);
      return company ? company.id : undefined;
    };

    const handleCompanyChange = companyIds => {
      filters.value.rootGroup.filters = filters.value.rootGroup.filters.filter(f => f.id !== 'companyId');
      if (!companyIds || companyIds.includes('VIEW_ALL') || companyIds.length === 0) {
        store.commit('pagesStore/manager/stemdoers/setCompanyFilter', companyIds);
        return;
      }
      filters.value.add({
        id: 'companyId',
        filter: entity => {
          const project = entity.currentProject;
          if (!project) return false;
          const companyId = getCompanyIdByProject(project.projectId);
          return companyId ? companyIds.includes(companyId) : false;
        },
        toString: () => 'companyId'
      });
      store.commit('pagesStore/manager/stemdoers/setCompanyFilter', companyIds);
    };

    const handleProjectChange = projectIds => {
      filters.value.rootGroup.filters = filters.value.rootGroup.filters.filter(f => f.id !== 'projectId');
      if (!projectIds || projectIds.includes('VIEW_ALL') || projectIds.length === 0) {
        store.commit('pagesStore/manager/stemdoers/setProjectFilter', projectIds);
        return;
      }
      filters.value.add({
        id: 'projectId',
        filter: entity => {
          const project = entity.currentProject;
          if (!project) return false;
          return projectIds.includes(project.projectId);
        },
        toString: () => 'projectId'
      });
      store.commit('pagesStore/manager/stemdoers/setProjectFilter', projectIds);
    };

    return {
      filteredStemdoers,
      stemdoers,
      displaySkeleton,
      companyItems,
      projectItems,
      handleVerticalChange,
      handleStatusChange,
      handleSearchChange,
      handleCompanyChange,
      handleProjectChange
    };
  }
});
